import React, { useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useDropzone } from "react-dropzone";
import styled, { css } from "styled-components";
import { Theme } from "../theme";
import { hex2rgba } from "../utils";
import { PencilIcon } from "./icons/PencilIcon";
import PropTypes from "prop-types";

const UspsDropzoneContainer = styled.div`
    position: relative;
    border: 1px dashed ${Theme.colors.blue};
    border-radius: 5px;
    padding: 0.65rem 1.3rem;
    max-width: ${(props) => (props.maxWidth ? props.maxWidth : "100%")};
    max-height: ${(props) => (props.maxHeight ? props.maxHeight : "100%")};
    width: 100%;
    height: 100%;

    ${(props) =>
        props.circle &&
        css`
            display: inline-block;
            border-radius: 50%;

            padding: 10px;

            img {
                border-radius: 50%;
            }
        `}

    ${(props) =>
        props.hasFile &&
        css`
            padding: 0;
        `}

    img {
        padding: ${(props) => props.enablePadding && "20px 50px"};
        max-width: ${(props) => (props.maxWidth ? `${props.maxWidth}px` : "100%")};
        max-height: ${(props) => (props.maxHeight ? `${props.maxHeight}px` : "100%")};
        width: 100%;
        height: 100%;
    }
    text-align: center;
    transition: 0.1s all cubic-bezier(0, 0.75, 0.05, 0.98);
    cursor: pointer;

    p {
        appearance: none;
        border: none;
        background-color: transparent;
        font-weight: bold;
        color: ${Theme.colors.blue};
    }

    &:hover {
        background-color: rgba(51, 51, 102, 0.1);
    }

    @keyframes reject-upload {
        0% {
            background-color: rgba($red, 0.1);
        }
        10%,
        90% {
            transform: translate3d(-0.5px, 0, 0);
        }

        20%,
        80% {
            transform: translate3d(1px, 0, 0);
        }

        30%,
        50%,
        70% {
            transform: translate3d(-2px, 0, 0);
        }

        40%,
        60% {
            transform: translate3d(2px, 0, 0);
        }
        100% {
            background-color: rgba($blue, 0);
        }
    }

    ${(props) =>
        (props.active || props.accepted) &&
        css`
            transform: scale(1.05);
            background-color: ${hex2rgba(Theme.colors.blue, 0.1)};
        `}

    ${(props) =>
        props.rejected &&
        css`
            color: ${Theme.colors.red};
            animation: reject-upload 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
            border: 1px dashed ${Theme.colors.red};
        `}
`;

const EditButton = styled.button.attrs({ type: "button" })`
    position: absolute;
    cursor: pointer;
    right: -15px;
    bottom: -15px;
    background-color: #3573b1;
    border-radius: 50%;
    appearance: none;
    border: none;
    width: 30px;
    height: 30px;

    ${(props) =>
        props.circle &&
        css`
            right: 0;
            bottom: 0;
        `}
`;

export const UspsDropzone = React.forwardRef(
    (
        {
            maxFileSize,
            onExceedsMaxFileSize,
            previousFileName,
            onDrop,
            onRejected,
            accept,
            currentImage,
            children,
            onEdit,
            hideEdit,
            maxWidth,
            maxHeight,
            enablePadding,
            ...props
        },
        ref
    ) => {
        const [, setFile] = useState(null);
        const [, setBase64] = useState("");
        const [, setFileDropped] = useState(false);
        const [, setPreviousUploadFileName] = useState(previousFileName);

        const onFileDrop = useCallback(
            (acceptedFiles) => {
                return new Promise((resolve, reject) => {
                    const file = acceptedFiles[0];
                    const reader = new FileReader();
                    const maxSize = maxFileSize || -1;

                    // Allow upload if it is under the max file size, or if no max file size was provided.
                    if (maxSize === -1 || file.size <= maxSize) {
                        setFile(file);
                        setFileDropped(true);
                        setPreviousUploadFileName(file.name);

                        reader.onload = () => {
                            const base64 = reader.result.split(",")[1];
                            setBase64(base64);
                            resolve(reader.result);
                        };
                        reader.onabort = () => reject("file reading was aborted");
                        reader.onerror = () => reject("file reading has failed");
                        reader.readAsDataURL(file);
                    } else {
                        if (onExceedsMaxFileSize) {
                            onExceedsMaxFileSize();
                        }
                    }
                });
            },
            [maxFileSize, onExceedsMaxFileSize]
        );
        const handleFiles = useCallback(
            (acceptedFiles) => {
                onFileDrop(acceptedFiles).then((res) => {
                    if (onDrop) {
                        onDrop(acceptedFiles, res);
                    }
                });
            },
            [onDrop, onFileDrop]
        );
        const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, fileRejections, ...rest } =
            useDropzone({
                accept: accept,
                onDrop: handleFiles,
            });

        useImperativeHandle(ref, () => ({
            open() {
                rest.open();
            },
        }));

        useEffect(() => {
            if (isDragReject || fileRejections.length) {
                if (onRejected) {
                    onRejected();
                }
            }
        }, [isDragReject, fileRejections]);

        return (
            <UspsDropzoneContainer
                {...getRootProps({ isDragActive, isDragAccept, isDragReject })}
                hasFile={!!currentImage}
                active={isDragActive}
                accepted={isDragAccept}
                rejected={isDragReject || fileRejections.length}
                enablePadding={enablePadding}
                maxWidth={maxWidth}
                maxHeight={maxHeight}
                {...props}
            >
                <input {...getInputProps()} />
                {currentImage ? (
                    <>
                        <img src={currentImage} alt="Uploaded File" />
                        {!hideEdit && (
                            <EditButton
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (onEdit) {
                                        onEdit();
                                    }
                                }}
                                circle={props.circle}
                            >
                                <PencilIcon />
                            </EditButton>
                        )}
                    </>
                ) : (
                    <div>{children}</div>
                )}
            </UspsDropzoneContainer>
        );
    }
);

UspsDropzone.propTypes = {
    /**
     * The max file size allowed in bytes
     */
    maxFileSize: PropTypes.number,
    /**
     * Callback if file exceeds max file size
     */
    onExceedsMaxFileSize: PropTypes.func,
    /**
     * The current / previous file name selecte
     */
    previousFileName: PropTypes.string,
    /**
     * Callback when a file is dropped.
     */
    onDrop: PropTypes.func,
    /**
     * Callback when a dropped file is rejected
     */
    onRejected: PropTypes.func,
    /**
     * The type of files accepted.
     */
    accept: PropTypes.string,
    /**
     * Base64 encoded image data
     */
    currentImage: PropTypes.string,
    /**
     * Callback when selecting edit.
     */
    onEdit: PropTypes.func,
    children: PropTypes.node,
};
